import React, { useState } from 'react'
import moment from 'moment'
import { Message } from './types'
import outgoingCallIcon from 'images/outgoing-call.svg'
import incomingCallIcon from 'images/incoming-call.svg'
import missedCallIcon from 'images/missed-call.svg'
import SimpleCallModal from './simple-call-modal'

interface BasicCallCardProps {
  message: Message
  showTimestamp: boolean
  formatTimestamp: (timestamp: string) => string
}

const BasicCallCard: React.FC<BasicCallCardProps> = ({
  message,
  showTimestamp,
  formatTimestamp,
}) => {
  const [showModal, setShowModal] = useState(false)

  const isOutbound = message.call?.direction === 'outbound'
  // Missed call
  const isMissedCall =
    message.call?.inbound &&
    (message.call?.status === 'no-answer' || message.call?.status === 'busy')

  // Get contact name from user_contact if available
  const contactName =
    message.call?.user_contact?.contact?.full_name ||
    message.call?.caller_name ||
    message.call?.user_contact?.phone_number ||
    (message.call?.inbound ? message.call?.from : message.call?.to) ||
    'Unknown Contact'

  //  recording availability check
  const hasRecording =
    message.call?.recording_url && message.call?.recording_data?.transcribed

  //  click handler pattern
  const handleCallClick = () => {
    if (hasRecording) {
      // Show transcript modal
      setShowModal(true)
    }
  }

  const getCallIcon = () => {
    if (isMissedCall) return missedCallIcon
    return isOutbound ? outgoingCallIcon : incomingCallIcon
  }

  const getCallMessage = () => {
    const durationText = message.call?.duration
      ? ` - ${Math.floor(message.call.duration / 60)}:${Math.floor(
          message.call.duration % 60
        )
          .toString()
          .padStart(2, '0')}`
      : ''

    if (isMissedCall) {
      return `Missed call from ${contactName}`
    }

    return isOutbound
      ? `Call to ${contactName}${durationText}`
      : `Call from ${contactName}${durationText}`
  }

  return (
    <div>
      {showTimestamp && (
        <div className=" text-center my-2">
          {formatTimestamp(message.sent_at)}
        </div>
      )}

      <div className="d-flex justify-content-center mb-3">
        <small
          className="text-white px-3 py-2 rounded-pill d-inline-flex flex-wrap align-items-center justify-content-center"
          style={{
            cursor: hasRecording ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            lineHeight: '1.5',
          }}
          onClick={handleCallClick}
          onMouseEnter={(e) => {
            if (hasRecording) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'
            }
          }}
          onMouseLeave={(e) => {
            if (hasRecording) {
              e.currentTarget.style.backgroundColor = 'transparent'
            }
          }}
        >
          <img
            src={getCallIcon()}
            alt={
              isMissedCall
                ? 'Missed Call'
                : isOutbound
                ? 'Outgoing Call'
                : 'Incoming Call'
            }
            style={{
              marginRight: '8px',
              width: 'clamp(18px, 5vw, 20px)',
              height: 'clamp(18px, 5vw, 20px)',
            }}
          />
          <span
            style={{
              fontSize: 'clamp(14px, 4vw, 16px)',
              marginRight: '8px',
            }}
          >
            {getCallMessage()}
          </span>
          {/*  recording indicator */}
          {hasRecording && (
            <ion-icon
              name="information-circle-outline"
              style={{
                fontSize: 'clamp(14px, 4vw, 16px)',
                opacity: 0.7,
                marginRight: '8px',
              }}
            />
          )}
          <span
            className="text-nowrap"
            style={{
              fontSize: 'clamp(14px, 4vw, 16px)',
              opacity: 0.8,
            }}
          >
            •{' '}
            {moment(message.sent_at).calendar(null, {
              sameDay: '[today at] h:mm A',
              lastDay: '[yesterday at] h:mm A',
              lastWeek: 'dddd [at] h:mm A',
              sameElse: 'M/D/YYYY [at] h:mm A',
            })}
          </span>
        </small>
      </div>

      {/* Simple Call Modal */}
      <SimpleCallModal
        show={showModal}
        onClose={() => setShowModal(false)}
        message={message}
      />
    </div>
  )
}

export default BasicCallCard
